import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  Box, Button, Divider, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Select, Stack, Switch, TextField,
  Typography, FormControlLabel, Chip,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { StoreUser } from "api/users";
import { GetStationDetailById, GetStationList } from "api/stations";
import { getAllCustomerGroups } from "api/customerGroup";
import { GetRoleList } from "api/role";
import { GetAgentList } from "api/agent";
import { playCorrectSound, playIncorrectSound } from "utils";
import { checkSession } from "utils/auth";

const NewUser = ({ onCreated } = {}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [stations, setStations] = useState([]);
  const [stationList, setStationList] = useState([]);
  const [selectedStation, setSelectedStation] = useState("");
  const [whatsappInstances, setWhatsappInstances] = useState([]);
  const [instanceList, setInstanceList] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [remark, setRemark] = useState("");
  const [roles, setRoles] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [agentList, setAgentList] = useState([]);
  const [allowedSeeAllCustomers, setAllowedSeeAllCustomers] = useState(false);
  const [allowedSeeAllPackages, setAllowedSeeAllPackages] = useState(false);

  const clearForm = () => {
    setName(""); setPassword(""); setEmail(""); setWhatsapp("");
    setRole("user"); setStations([]); setWhatsappInstances([]); setRemark(""); setAgentId("");
    setAllowedSeeAllCustomers(false); setAllowedSeeAllPackages(false);
  };

  useEffect(() => {
    checkSession();
    GetStationList().then((res) => { if (res.success) setStationList(res.data); });
    getAllCustomerGroups().then((res) => { if (res.success) setInstanceList(res.data || []); });
    GetRoleList().then((res) => { if (res.status === 200) setRoles(res.data); });
    GetAgentList().then((res) => { if (res?.success) setAgentList(res.data || []); });
  }, []);

  const handleAddStation = () => {
    GetStationDetailById(selectedStation).then((res) => {
      if (!res.success) return;
      const newStation = { id: res.data.id, name: res.data.name, default: stations.length === 0 };
      setStations((prev) => [...prev, newStation]);
      setStationList((prev) => prev.filter((s) => s.id !== res.data.id));
      const remaining = stationList.filter((s) => s.id !== res.data.id);
      setSelectedStation(remaining.length > 0 ? remaining[0].id : "");
    });
  };

  const handleRemoveStation = (station) => {
    setStations((prev) => {
      const next = prev.filter((s) => s.id !== station.id);
      if (station.default && next.length > 0) next[0].default = true;
      return next;
    });
    setStationList((prev) => [...prev, station]);
  };

  const handleToggleDefault = (stationId) => {
    setStations((prev) =>
      prev.map((s) => ({ ...s, default: s.id === stationId }))
    );
  };

  const handleAddInstance = () => {
    const group = instanceList.find((g) => g.id === selectedInstance);
    if (!group) return;
    const newInstance = { id: group.id, name: group.name, default: whatsappInstances.length === 0 };
    setWhatsappInstances((prev) => [...prev, newInstance]);
    setInstanceList((prev) => prev.filter((g) => g.id !== newInstance.id));
    const remaining = instanceList.filter((g) => g.id !== newInstance.id);
    setSelectedInstance(remaining.length > 0 ? remaining[0].id : "");
  };

  const handleRemoveInstance = (instance) => {
    setWhatsappInstances((prev) => {
      const next = prev.filter((w) => w.id !== instance.id);
      if (instance.default && next.length > 0) next[0].default = true;
      return next;
    });
    setInstanceList((prev) => [...prev, instance]);
  };

  const handleToggleDefaultInstance = (instanceId) => {
    setWhatsappInstances((prev) =>
      prev.map((w) => ({ ...w, default: w.id === instanceId }))
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    StoreUser(name, email, password, whatsapp, role, stations, remark, whatsappInstances, role === "agent" ? agentId : "",
      role === "agent" && allowedSeeAllCustomers, role === "agent" && allowedSeeAllPackages).then((res) => {
      if (res.success) {
        clearForm();
        playCorrectSound();
        Swal.fire({ title: "Created!", icon: "success", text: `${name} has been created`, timer: 1500 })
          .then(() => { if (onCreated) onCreated(); });
      } else {
        playIncorrectSound();
        Swal.fire({ title: "Error!", icon: "error", text: res?.data?.message || res?.message, showConfirmButton: true });
      }
    });
  };

  const canSubmit = stations.length > 0 && name && whatsapp && email && password && (role !== "agent" || agentId);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {/* Name & WhatsApp */}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Name *" value={name}
            onChange={(e) => setName(e.target.value)} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="WhatsApp *" value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)} required />
        </Grid>

        {/* Email & Password */}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Email *" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Password *" type="password" value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)} required />
        </Grid>

        {/* Role */}
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Role *</InputLabel>
            <Select value={role} label="Role *" onChange={(e) => setRole(e.target.value)} required>
              {roles.map(({ role, description }) => (
                <MenuItem key={role} value={role}>
                  <Typography variant="body2">
                    <strong>{role.toUpperCase()}</strong>
                    {description ? ` — ${description}` : ""}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Agent — required for role=agent; scopes the user's customers/packages */}
        {role === "agent" && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Agent *</InputLabel>
              <Select value={agentId} label="Agent *" onChange={(e) => setAgentId(e.target.value)} required>
                {agentList.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.id} — {a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {role === "agent" && (
          <Grid item xs={12}>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Switch color="secondary" size="small" checked={allowedSeeAllCustomers} onChange={(e) => setAllowedSeeAllCustomers(e.target.checked)} />}
                label={<Typography variant="body2">Can see all customers</Typography>}
              />
              <FormControlLabel
                control={<Switch color="secondary" size="small" checked={allowedSeeAllPackages} onChange={(e) => setAllowedSeeAllPackages(e.target.checked)} />}
                label={<Typography variant="body2">Can see and manage all packages</Typography>}
              />
            </Stack>
          </Grid>
        )}

        {/* Remark */}
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Remark" value={remark} multiline maxRows={3}
            onChange={(e) => setRemark(e.target.value)} />
        </Grid>

        {/* Station Picker */}
        <Grid item xs={12}>
          <Divider>
            <Typography variant="caption" color="text.secondary">Stations</Typography>
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth size="small">
              <InputLabel>Select Station</InputLabel>
              <Select value={selectedStation} label="Select Station"
                onChange={(e) => setSelectedStation(e.target.value)}>
                {stationList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>{id} — {name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" color="secondary" size="small"
              startIcon={<AddIcon />} onClick={handleAddStation}
              disabled={!selectedStation} sx={{ whiteSpace: "nowrap", px: 2, py: 1 }}>
              Add
            </Button>
          </Stack>
        </Grid>

        {/* Station List */}
        {stations.length > 0 && (
          <Grid item xs={12}>
            <Stack spacing={0.75}>
              {stations.map((s) => (
                <Box key={s.id} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.75, borderRadius: 1,
                  border: "1px solid", borderColor: "divider",
                }}>
                  <Chip label={s.id} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: "0.72rem" }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.name}</Typography>
                  <FormControlLabel
                    control={
                      <Switch size="small" color="secondary"
                        checked={s.default} disabled={stations.length === 1}
                        onChange={() => handleToggleDefault(s.id)} />
                    }
                    label={<Typography variant="caption">{s.default ? "Default" : ""}</Typography>}
                    sx={{ mr: 0 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveStation(s)}
                    sx={{ "&:hover": { color: "error.main" } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Grid>
        )}

        {stations.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              ยังไม่ได้เพิ่ม Station — ต้องเพิ่มอย่างน้อย 1 Station
            </Typography>
          </Grid>
        )}

        {/* WhatsApp Instance Picker — optional, not required to create the user */}
        <Grid item xs={12}>
          <Divider>
            <Typography variant="caption" color="text.secondary">WhatsApp Instances (WhatsApp Chat menu)</Typography>
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth size="small">
              <InputLabel>Select Instance</InputLabel>
              <Select value={selectedInstance} label="Select Instance"
                onChange={(e) => setSelectedInstance(e.target.value)}>
                {instanceList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" color="secondary" size="small"
              startIcon={<AddIcon />} onClick={handleAddInstance}
              disabled={!selectedInstance} sx={{ whiteSpace: "nowrap", px: 2, py: 1 }}>
              Add
            </Button>
          </Stack>
        </Grid>

        {whatsappInstances.length > 0 && (
          <Grid item xs={12}>
            <Stack spacing={0.75}>
              {whatsappInstances.map((w) => (
                <Box key={w.id} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.75, borderRadius: 1,
                  border: "1px solid", borderColor: "divider",
                }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{w.name}</Typography>
                  <FormControlLabel
                    control={
                      <Switch size="small" color="secondary"
                        checked={w.default} disabled={whatsappInstances.length === 1}
                        onChange={() => handleToggleDefaultInstance(w.id)} />
                    }
                    label={<Typography variant="caption">{w.default ? "Default" : ""}</Typography>}
                    sx={{ mr: 0 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveInstance(w)}
                    sx={{ "&:hover": { color: "error.main" } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Grid>
        )}

        {/* Submit */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button variant="outlined" color="secondary" onClick={clearForm}>Clear</Button>
            <Button variant="contained" color="secondary" type="submit" disabled={!canSubmit}>
              Create User
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewUser;
